/**
 * @ghosty/branding — the deterministic design-token engine.
 *
 * A company onboards and picks a theme mode (light/dark) + a primary brand
 * colour (+ optional secondary) + a font pairing. From that, derivePalette()
 * produces a full, accessibility-checked DesignTokens set used identically by:
 *   - the onboarding wizard (live preview, client-side),
 *   - the marketplace + admin surfaces (runtime),
 *   - the scaffolded-app templates (baked at scaffold time).
 *
 * Framework-agnostic (Node AND browser), zero runtime dependencies, no
 * Math.random — same input, same output, everywhere.
 */

export type { BrandMode, BrandInput, DesignTokens } from './palette.js';
export { derivePalette } from './palette.js';

export type { FontPairing, FontFace } from './fonts.js';
export { FONT_PAIRINGS, fontPairing, googleFontsUrl, DEFAULT_FONT_KEY } from './fonts.js';

import type { BrandMode, DesignTokens } from './palette.js';
import { DEFAULT_FONT_KEY } from './fonts.js';

/** Ghosty's own defaults ("clean & airy" rebrand, 2026-07): light mode,
 *  emerald primary, DM Sans. The secondary stays EXPLICIT (soft sky blue):
 *  left to derivePalette's 150° hue rotation the primary lands somewhere
 *  unhelpful. Custom brands that set only a primary still get the derived
 *  secondary — this pin is house-only. Tenant apps that never customized
 *  their brand inherit these values as fallbacks. */
export const DEFAULT_BRAND: {
  mode: BrandMode;
  primary: string;
  secondary: string;
  fontKey: string;
} = {
  mode: 'light',
  primary: '#059669',
  secondary: '#8dc6f0',
  fontKey: DEFAULT_FONT_KEY,
};

/**
 * The console/control-tower house look — DESIGNED tokens, not derived.
 * derivePalette's light mode tints neutrals toward the brand hue (a feature
 * for tenant themes); the house design system wants cool slate neutrals with
 * the emerald reserved for actions. Same shape as derivePalette's output so
 * applyHouseTheme can consume either.
 */
export const HOUSE_TOKENS: DesignTokens = {
  mode: 'light',
  bg: '#ffffff',
  surface: '#f8fafc',
  surface2: '#f1f5f9',
  line: '#e2e8f0',
  text: '#0f172a',
  // slate-600: most console copy is muted text, so it must clear AA with
  // room to spare (7.6:1) — slate-500 read washed out ("balanced slate",
  // contrast iteration 2026-07-09)
  muted: '#475569',
  accent: '#059669',
  accentHover: '#047857',
  onAccent: '#ffffff',
  secondary: '#f1f5f9',
  onSecondary: '#0f172a',
  success: '#16a34a',
  // amber-700: amber-600 on the warning wash failed WCAG outright (~2.6:1)
  warning: '#b45309',
  danger: '#dc2626',
  focus: '#059669',
};
